import React, { useState } from 'react';
import { BackButton } from '../Components';
import { Checklist } from './Checklist';
import { RevisionDetails } from './RevisionDetails';
import { RevisionFreeText } from './RevisionFreeText';
import { ProgressResponsibility } from './ProgressResponsibility';
import { ProjectLeadAssessment } from './ProjectLeadAssessment';
import { CheckItems } from './CheckItems';
import { EditableTextField } from '../components/EditableTextField';
import { REVISION_BY_ID, GET_CHANGES, UPDATE_WORKING_TITLE } from './revisions.graphql';
import { useMutation } from '@apollo/client';
import { useQuery } from '@apollo/client';
import { Typography, Grid2 as Grid } from '@mui/material';
import { Changes } from '../components/Changes';
import LoadingSpinner from '../LoadingSpinner';
import { Link, useParams } from 'react-router-dom';
import { Roles } from './Roles';
import { GetRevisionQuery } from '../__generated__/graphql';
import { Unpacked } from '../graphQLTypes/types';
import { Box } from '@mui/system';

export type Revision = Unpacked<GetRevisionQuery['revision']>;

export function Details() {
  const { id } = useParams();
  const [validate, setValidate] = useState(false);
  const { data, loading } = useQuery(REVISION_BY_ID, { variables: { revisionId: id } });

  const revision = data?.revision;

  return (
    <>
      {loading && <LoadingSpinner />}
      {revision && (
        <Grid container sx={{ maxWidth: (theme) => theme.breakpoints.values.xl, mx: 'auto' }}>
          <Grid size={{ xs: 12 }}>
            <Title revision={revision} />
          </Grid>
          <Grid size={{ xs: 11 }} style={{ paddingLeft: 10 }}>
            <WorkingTitle revision={revision} />
          </Grid>
          <Grid size={{ xs: 12, md: 7 }} sx={{ order: { xs: 2, md: 1 } }}>
            <ProgressResponsibility revision={revision} readonly={revision.completedAt ? true : false} />
            <ProjectLeadAssessment revision={revision} readonly={revision.completedAt ? true : false} />
            <Checklist revision={revision} history={revision.completedAt ? true : false} />
            <CheckItems revision={revision} history={revision.completedAt ? true : false} />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }} sx={{ order: { xs: 1, md: 2 } }}>
            <RevisionDetails revision={revision} readonly={revision.completedAt ? true : false} validate={validate} setValidate={setValidate} />
            <Roles revision={revision} history={revision.completedAt ? true : false} validate={validate} />
            <RevisionFreeText revision={revision} readonly={revision.completedAt ? true : false} />
            <Changes id={revision.id} gqlQuery={GET_CHANGES} title="Logg revisjon (auto)" />
          </Grid>
        </Grid>
      )}
    </>
  );
}

function Title({ revision }: { revision: Revision }) {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', md: 'center' }}
      paddingRight={2}
      flexDirection={{ xs: 'column', md: 'row' }}>
      <Box display="flex" alignItems="center">
        <BackButton />
        <Typography variant="h5">{revisionTitle(revision)}</Typography>
      </Box>
      <Link to={`/guide/${revision?.guideId}`}>
        <span>Gå til anvisning</span>
      </Link>
    </Box>
  );
}

function revisionTitle(revision: Revision) {
  const guide = revision?.guide;
  return guide?.docName && guide?.docTitle ? `${guide.docName} ${guide.docTitle}` : revision?.title;
}

function WorkingTitle({ revision }: { revision: Revision }) {
  const [updateWorkingTitle] = useMutation(UPDATE_WORKING_TITLE);
  if (revision?.completedAt) {
    return <Typography component="h3">{revision.workingTitle ?? 'Ingen arbeidstittel'}</Typography>;
  }

  return (
    <Grid container direction="row" alignItems="center">
      <Grid style={{ paddingRight: 5 }}>
        <span>Arbeidstittel:</span>
      </Grid>
      <Grid>
        <EditableTextField
          value={revision?.workingTitle ?? ''}
          onUpdate={async (value) => {
            await updateWorkingTitle({ variables: { input: { id: revision?.id, title: value } } });
          }}
        />
      </Grid>
    </Grid>
  );
}
